import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { styled, useTheme } from '@mui/material/styles'
import {
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
} from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { debounce, orderBy } from 'lodash'

import { formatDate, formatPercentage } from '../../utils/functions'
import Popover from '../Popover/Popover'

const isEmptyOrNaN = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === '' ||
    (typeof value === 'number' && isNaN(value)) ||
    value === 'NaN'
  ) {
    return true
  }
  if (Array.isArray(value) && value.length === 0) return true
  if (typeof value === 'object' && Object.keys(value).length === 0) return true
  return false
}

const formatCellValue = (value) => {
  return isEmptyOrNaN(value) ? '--' : value
}

const Gap = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  height: 23,
  backgroundColor: '#FFFFFF',
  zIndex: 12,
  [theme.breakpoints.down('xs')]: {
    display: 'none',
  },
}))

const TableContainerStyled = styled('div')(({ theme }) => ({
  width: '100%',
  marginTop: '10px',
  backgroundColor: '#fff',
}))

const GridTable = styled('div', {
  shouldForwardProp: (prop) => prop !== 'gridTemplateColumns',
})(({ gridTemplateColumns }) => ({
  display: 'grid',
  width: '100%',
  borderCollapse: 'collapse',
  gridAutoRows: 'auto',
  gridTemplateColumns: gridTemplateColumns,
}))

const SpacerRow = styled('div')(({ theme }) => ({
  height: theme.spacing(0),
  gridColumn: '1 / -1',
}))

const TitleCell = styled('div')(({ theme, grayBackground }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  background: '#f3f3f3',
  textAlign: 'left',
  fontWeight: 'bold',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gridColumn: '1 / -1',
}))

const HeaderCell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'firstColumn' && prop !== 'sortable',
})(({ theme, firstColumn, sortable }) => ({
  backgroundColor: theme.palette.primary.light,
  fontWeight: theme.typography.fontWeightBold,
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  textAlign: firstColumn ? 'left' : 'center',
  verticalAlign: 'middle',
  fontSize: '16px',
  boxSizing: 'border-box',
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  wordWrap: 'break-word',
  whiteSpace: 'normal',
  textOverflow: 'ellipsis',
  cursor: sortable ? 'pointer' : 'default',
  display: 'flex',
  alignItems: 'center',
  justifyContent: firstColumn ? 'flex-start' : 'center',
  position: 'relative',
}))

const DataCell = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'alignRight' && prop !== 'footer' && prop !== 'grayBackground',
})(({ theme, alignRight, footer, grayBackground }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  textAlign: alignRight ? 'right' : 'center',
  verticalAlign: 'middle',
  boxSizing: 'border-box',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
  minWidth: 0,
  textOverflow: 'ellipsis',
  display: 'flex',
  alignItems: 'center',
  justifyContent: alignRight ? 'flex-end' : 'center',
  backgroundColor: grayBackground || footer ? '#ccc' : 'transparent',
}))

const FirstColumnCell = styled(DataCell, {
  shouldForwardProp: (prop) => prop !== 'footer' && prop !== 'grayBackground',
})(({ theme, footer, grayBackground }) => ({
  justifyContent: 'flex-start',
  textAlign: 'left',
  position: 'sticky',
  fontWeight: '500',
  left: 0,
  backgroundColor:
    grayBackground || footer ? '#ccc' : theme.palette.background.paper,
  zIndex: 2,
}))

const PaginationControls = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(1),
  gap: theme.spacing(2),
  '& button': {
    borderRadius: '20px',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
}))

const SortIconContainer = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(1),
  fontSize: '1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const DateStyleOverride = styled('span')(({ theme }) => ({
  fontWeight: 500,
  [theme.breakpoints.between('lg', 'xl')]: {
    fontSize: '13px',
  },
}))

const ScrollingTable = ({
  data,
  columns,
  Col1Width = '25%',
  footerData = [],
  options = {},
  lastColumnAlignRight = false,
  noRowPadding = false,
  className = '',
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'))
  // New: only between lg and xl we paginate desktop columns if > 10 columns
  const isDesktopPaginated = useMediaQuery(
    theme.breakpoints.between('lg', 'xl')
  )

  // Existing states for mobile/tablet pagination
  const [currentMobileColumn, setCurrentMobileColumn] = useState(1)
  const [currentTabletPage, setCurrentTabletPage] = useState(0)

  // New state for desktop pagination
  const [currentDesktopPage, setCurrentDesktopPage] = useState(0)

  const [firstColumnWidth, setFirstColumnWidth] = useState(Col1Width)
  const firstColumnRefs = useRef([])
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })

  const columnsLengthRef = useRef(columns.length)
  columnsLengthRef.current = columns.length

  // Tablet pagination: show 3 extra columns (first column fixed)
  const columnsPerTabletPage = 3
  // Desktop pagination: show 9 extra columns (first column fixed -> total 10 columns)
  const columnsPerDesktopPage = 9

  // Compute total pages for tablet view
  const totalTabletPages = useMemo(() => {
    if (isTablet && columns.length > 3) {
      return Math.ceil((columns.length - 1) / columnsPerTabletPage)
    }
    return 0
  }, [isTablet, columns.length])
  const totalTabletPagesRef = useRef(totalTabletPages)
  totalTabletPagesRef.current = totalTabletPages

  // Compute total pages for desktop pagination (only if > 10 columns)
  const totalDesktopPages = useMemo(() => {
    if (isDesktopPaginated && columns.length > 10) {
      return Math.ceil((columns.length - 1) / columnsPerDesktopPage)
    }
    return 0
  }, [isDesktopPaginated, columns.length])
  const totalDesktopPagesRef = useRef(totalDesktopPages)
  totalDesktopPagesRef.current = totalDesktopPages

  // Handlers for mobile pagination
  const handlePrevMobileColumn = useMemo(() => {
    return () => {
      setCurrentMobileColumn((prev) => Math.max(1, prev - 1))
    }
  }, [])
  const handleNextMobileColumn = useMemo(() => {
    return () => {
      setCurrentMobileColumn((prev) =>
        Math.min(columnsLengthRef.current - 1, prev + 1)
      )
    }
  }, [])

  // Handlers for tablet pagination
  const handlePrevTabletPage = useMemo(() => {
    return () => {
      setCurrentTabletPage((prev) => Math.max(0, prev - 1))
    }
  }, [])
  const handleNextTabletPage = useMemo(() => {
    return () => {
      setCurrentTabletPage((prev) =>
        Math.min(totalTabletPagesRef.current - 1, prev + 1)
      )
    }
  }, [])

  // Handlers for desktop pagination
  const handlePrevDesktopPage = useMemo(() => {
    return () => {
      setCurrentDesktopPage((prev) => Math.max(0, prev - 1))
    }
  }, [])
  const handleNextDesktopPage = useMemo(() => {
    return () => {
      setCurrentDesktopPage((prev) =>
        Math.min(totalDesktopPagesRef.current - 1, prev + 1)
      )
    }
  }, [])

  const adjustWidth = useMemo(() => {
    return debounce(() => {
      if (options.adjustFirstColumnWidth !== false && !isMobile && !isTablet) {
        if (firstColumnRefs.current.length > 0) {
          window.requestAnimationFrame(() => {
            const widths = firstColumnRefs.current.map((cell) =>
              cell ? cell.getBoundingClientRect().width : 0
            )
            if (widths.length > 0) {
              const maxWidth = Math.max(...widths)
              const finalWidth =
                maxWidth < parseInt(firstColumnWidth, 10)
                  ? `${maxWidth}px`
                  : firstColumnWidth
              if (finalWidth !== firstColumnWidth) {
                setFirstColumnWidth(finalWidth)
              }
            }
          })
        }
      }
    }, 100)
  }, [options.adjustFirstColumnWidth, isMobile, isTablet, firstColumnWidth])

  useEffect(() => {
    adjustWidth()
    let resizeObserver
    if (options.adjustFirstColumnWidth !== false && !isMobile && !isTablet) {
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          adjustWidth()
        })
        firstColumnRefs.current.forEach((cell) => {
          if (cell) {
            resizeObserver.observe(cell)
          }
        })
      }
    }
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [adjustWidth, options.adjustFirstColumnWidth, isMobile, isTablet])

  const mergedData = useMemo(() => {
    if (!footerData || footerData.length === 0) return data
    return [
      ...data,
      ...footerData.map((footerItem) => ({ ...footerItem, isFooter: true })),
    ]
  }, [data, footerData])

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return mergedData
    const column = columns.find((col) => col.id === sortConfig.key)
    if (!column) return mergedData
    return orderBy(
      mergedData,
      [
        (row) => {
          let value =
            typeof column.accessor === 'function'
              ? column.accessor(row)
              : row[column.accessor]
          return formatCellValue(value)
        },
      ],
      [sortConfig.direction]
    )
  }, [mergedData, sortConfig, columns])

  useEffect(() => {
    firstColumnRefs.current = []
  }, [sortedData, columns])

  const getVisibleColumns = () => {
    if (isMobile) {
      return columns.filter(
        (col, index) => index === 0 || index === currentMobileColumn
      )
    } else if (isTablet && columns.length > 3) {
      const start = currentTabletPage * columnsPerTabletPage + 1
      const end = start + columnsPerTabletPage
      return columns.filter(
        (col, index) => index === 0 || (index >= start && index < end)
      )
    } else if (isDesktopPaginated && columns.length > 10) {
      const start = currentDesktopPage * columnsPerDesktopPage + 1
      const end = start + columnsPerDesktopPage
      return columns.filter(
        (col, index) => index === 0 || (index >= start && index < end)
      )
    } else {
      return columns
    }
  }

  const visibleColumns = getVisibleColumns()

  const gridTemplateColumns = useMemo(() => {
    if (isMobile && columns.length === 1) {
      return '1fr'
    } else if (isMobile && columns.length > 1) {
      return 'repeat(2, 1fr)'
    } else if (isTablet && columns.length > 3) {
      const numVisible = visibleColumns.length
      return `repeat(${numVisible}, 1fr)`
    } else if (isDesktopPaginated && columns.length > 10) {
      const numVisible = visibleColumns.length
      return `${firstColumnWidth} repeat(${numVisible - 1}, 1fr)`
    } else {
      return `${firstColumnWidth} repeat(${visibleColumns.length - 1}, 1fr)`
    }
  }, [
    isMobile,
    isTablet,
    isDesktopPaginated,
    columns.length,
    visibleColumns.length,
    firstColumnWidth,
  ])

  const handleSort = (sortKey) => {
    let direction = 'asc'
    if (sortConfig.key === sortKey && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key: sortKey, direction })
  }

  const onHeaderClick = useMemo(
    () => (e) => {
      const sortable = e.currentTarget.getAttribute('data-sortable') === 'true'
      const sortKey = e.currentTarget.getAttribute('data-sortkey')
      if (sortable && sortKey) {
        handleSort(sortKey)
      }
    },
    [handleSort, sortConfig]
  )

  const onHeaderKeyUp = useMemo(
    () => (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.currentTarget) {
        const sortable =
          e.currentTarget.getAttribute('data-sortable') === 'true'
        const sortKey = e.currentTarget.getAttribute('data-sortkey')
        if (sortable && sortKey) {
          handleSort(sortKey)
        }
      }
    },
    [handleSort, sortConfig]
  )

  const renderHeaderContent = (column) => {
    const isSorted = sortConfig.key === column.id
    return (
      <Fragment>
        <span>{column.Header}</span>
        {column.popover && <Popover content={column.popover.content} />}
        {column.sortable && isSorted && (
          <SortIconContainer>
            {sortConfig.direction === 'asc' ? (
              <ArrowUpwardIcon fontSize="small" />
            ) : (
              <ArrowDownwardIcon fontSize="small" />
            )}
          </SortIconContainer>
        )}
      </Fragment>
    )
  }

  const addFirstColumnRef = (el) => {
    if (el) firstColumnRefs.current.push(el)
  }

  return (
    <>
      <Gap />
      <TableContainerStyled className={className}>
        <GridTable
          aria-label="Data table"
          role="table"
          gridTemplateColumns={gridTemplateColumns}
        >
          {visibleColumns.map((column, index) => (
            <HeaderCell
              role="row"
              key={`header-${column.id || index}`}
              firstColumn={index === 0}
              sortable={!!column.sortable}
            >
              <div
                tabIndex={column.sortable ? 0 : undefined}
                aria-sort={
                  sortConfig.key === column.id
                    ? sortConfig.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
                data-sortable={column.sortable ? 'true' : 'false'}
                data-sortkey={column.id || ''}
                onClick={onHeaderClick}
                onKeyUp={onHeaderKeyUp}
                role="columnheader"
              >
                {renderHeaderContent(column)}
              </div>
            </HeaderCell>
          ))}

          {sortedData.map((row, rowIndex) => {
            if (row.innerTitle) {
              return (
                <Fragment key={`header-${rowIndex}`}>
                  <SpacerRow />
                  <TitleCell grayBackground={row.grayBackground}>
                    <Typography variant="h6">{row.innerTitle}</Typography>
                    {row.isLoading && (
                      <CircularProgress size={20} color="primary" />
                    )}
                  </TitleCell>
                </Fragment>
              )
            }

            return (
              <Fragment key={`row-${rowIndex}`}>
                {visibleColumns.map((column, colIndex) => {
                  let cellValue =
                    typeof column.accessor === 'function'
                      ? column.accessor(row)
                      : row[column.accessor]
                  cellValue = formatCellValue(cellValue)
                  const isLastColumn =
                    lastColumnAlignRight &&
                    colIndex === visibleColumns.length - 1
                  const isFirstColumn = colIndex === 0
                  const CellRenderer =
                    typeof column.Cell === 'undefined'
                      ? ({ value }) => value
                      : column.Cell

                  const columnIdString = String(column.id).toLowerCase()
                  const formattedValue =
                    columnIdString.includes('date') && cellValue !== '--'
                      ? formatDate(cellValue)
                      : (columnIdString.includes('ratio') ||
                            columnIdString.includes('return')) &&
                          typeof cellValue !== 'string' &&
                          !isNaN(Number(cellValue))
                        ? formatPercentage(cellValue)
                        : cellValue

                  if (isFirstColumn) {
                    return (
                      <FirstColumnCell
                        key={`cell-${rowIndex}-${colIndex}`}
                        ref={addFirstColumnRef}
                        footer={row.isFooter}
                        grayBackground={row.grayBackground}
                      >
                        {CellRenderer({ value: formattedValue, row, column })}
                      </FirstColumnCell>
                    )
                  } else {
                    return (
                      <DataCell
                        key={`cell-${rowIndex}-${colIndex}`}
                        alignRight={isLastColumn}
                        footer={row.isFooter}
                        grayBackground={row.grayBackground}
                      >
                        {CellRenderer({ value: formattedValue, row, column })}
                      </DataCell>
                    )
                  }
                })}
              </Fragment>
            )
          })}
        </GridTable>
      </TableContainerStyled>

      {/* Pagination Controls */}
      {(isMobile ||
        isTablet ||
        (isDesktopPaginated && columns.length > 10)) && (
        <PaginationControls>
          {isTablet && totalTabletPages > 0 && (
            <Fragment>
              <Button
                onClick={handlePrevTabletPage}
                disabled={currentTabletPage === 0}
              >
                Prev
              </Button>
              <Button
                onClick={handleNextTabletPage}
                disabled={currentTabletPage >= totalTabletPages - 1}
              >
                Next
              </Button>
            </Fragment>
          )}
          {isMobile && columnsLengthRef.current > 2 && (
            <Fragment>
              <Button
                onClick={handlePrevMobileColumn}
                disabled={currentMobileColumn <= 1}
              >
                Prev
              </Button>
              <Button
                onClick={handleNextMobileColumn}
                disabled={currentMobileColumn >= columnsLengthRef.current - 1}
              >
                Next
              </Button>
            </Fragment>
          )}
          {isDesktopPaginated && columns.length > 10 && (
            <Fragment>
              <Button
                onClick={handlePrevDesktopPage}
                disabled={currentDesktopPage === 0}
              >
                Prev
              </Button>
              <Button
                onClick={handleNextDesktopPage}
                disabled={currentDesktopPage >= totalDesktopPages - 1}
              >
                Next
              </Button>
            </Fragment>
          )}
        </PaginationControls>
      )}
    </>
  )
}

export const dateRenderer = ({ value }) => (
  <DateStyleOverride>{value ? formatDate(value) : '--'}</DateStyleOverride>
)

export const percentageRenderer = ({ value }) => (
  <span>{value !== '--' ? formatPercentage(value) : '--'}</span>
)

ScrollingTable.propTypes = {
  data: PropTypes.any,
  columns: PropTypes.any,
  Col1Width: PropTypes.any,
  footerData: PropTypes.any,
  options: PropTypes.any,
  lastColumnAlignRight: PropTypes.bool,
  noRowPadding: PropTypes.bool,
  className: PropTypes.string,
}

export default React.memo(ScrollingTable)
